import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import WhatsAppButton from "@/components/WhatsAppButton";
import {
  Flame,
  Waves,
  Truck,
  ShieldCheck,
  Check,
  Star,
  Timer,
  Package,
  RotateCcw,
  Lock,
  ArrowRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useCart } from "@/context/CartContext";
import { findStaticProduct } from "@/lib/products";
import { toast } from "sonner";
import { trackViewContent } from "@/lib/metaPixel";
import InstagramReelCard from "@/components/InstagramReelCard";
import { getPromoPricing, getPromoBanner } from "@/lib/promo";

const SITE_URL = "https://myflovea.com";

const PromoCoussin = () => {
  const product = findStaticProduct("coussin-massant-chauffant");
  const [variantId, setVariantId] = useState("tissu-standard");
  const { addItem } = useCart();

  const variant = useMemo(
    () => product?.variants.find((v) => v.id === variantId) || product?.variants[0],
    [product, variantId]
  );

  useEffect(() => {
    document.documentElement.classList.add("promo-lp");
    return () => document.documentElement.classList.remove("promo-lp");
  }, []);

  // Meta Pixel — ViewContent for the LP (fires when variant changes / first render)
  useEffect(() => {
    if (!product || !variant) return;
    trackViewContent({
      id: `${product.id}::${variant.id}`,
      name: `Promo LP — Coussin ${variant.name?.fr || variant.id}`,
      price: variant.price,
      currency: "EUR",
    });
  }, [product?.id, variant?.id]);

  if (!product || !variant) return null;

  const heroImage = "/photos/coussin/coussin-voiture.webp";
  const seatImage = "/photos/coussin/coussin-siege-velour.webp";
  const remoteImage = "/photos/coussin/coussin-telecommande.webp";
  const officeImage = "/photos/hero-myflovea.webp";

  const buyNow = () => {
    addItem(product, variant, 1);
    toast.success("Ajouté au panier — direction Checkout");
    setTimeout(() => {
      window.location.href = "/checkout";
    }, 400);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Coussin Massant Chauffant myFLOVEA",
    image: [heroImage, seatImage, remoteImage],
    description:
      "Coussin Massant Chauffant myFLOVEA : 9 modes de massage, chaleur apaisante 45°C, s'adapte à tous les sièges. Expédié depuis l'Allemagne en 3 à 7 jours.",
    brand: { "@type": "Brand", name: "myFLOVEA" },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: "79.00",
      highPrice: "89.00",
      offerCount: 2,
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/promo-coussin`,
    },
  };

  return (
    <div
      data-testid="promo-coussin-page"
      className="min-h-screen bg-[#FAF6F0] text-[#1A1A1A] font-sans"
    >
      <Helmet>
        <title>Coussin Massant Chauffant — Livré en 3 à 7 jours depuis l'Europe</title>
        <meta
          name="description"
          content="Détends ton dos en 10 minutes. 9 modes de massage + chaleur 45°C. Expédié d'Allemagne en 3 à 7 jours. Satisfait ou remboursé 30 jours. -12% aujourd'hui."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/promo-coussin`} />
        <link rel="preload" as="image" href={heroImage} fetchpriority="high" />
        <meta property="og:title" content="Détends ton dos en 10 minutes — Coussin Massant Chauffant myFLOVEA" />
        <meta property="og:description" content="9 modes de massage + chaleur 45°C. Expédié d'Allemagne en 3 à 7 jours." />
        <meta property="og:image" content={heroImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Minimal top bar */}
      <header className="bg-[#3D2B1F] text-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-3 flex items-center justify-between gap-4">
          <Link
            to="/promo-coussin"
            data-testid="promo-logo"
            className="font-serif-display text-lg md:text-xl tracking-tight"
          >
            myFLOVEA
          </Link>
          <div className="hidden sm:flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase font-light">
            <Truck size={14} strokeWidth={1.6} />
            Livré en 3 à 7 jours · Satisfait ou remboursé 30 jours
          </div>
          <div className="sm:hidden text-[11px] font-medium tracking-wider uppercase">
            🚚 3–7 jours
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-[#dcd7ce]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-10 md:py-16 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="order-2 md:order-1">
            <div className="inline-flex items-center gap-2 bg-[#EFE7DA] border border-[#dcd7ce] text-[#3D2B1F] px-3 py-1.5 rounded-full text-[11px] tracking-[0.16em] uppercase mb-6">
              <span className="w-2 h-2 rounded-full bg-[#B45C3F] animate-pulse" />
              Stock disponible en Europe · Expédié sous 24h ouvrées
            </div>
            <h1
              data-testid="promo-h1"
              className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A]"
            >
              Détends ton dos en 10 minutes,
              <br />
              <span className="italic text-[#8B6343]">chez toi.</span>
            </h1>
            <p className="mt-6 text-[17px] md:text-[19px] leading-relaxed text-[#3D2B1F] font-light max-w-lg">
              <strong className="font-medium">9 modes de massage</strong> + chaleur
              apaisante jusqu'à <strong className="font-medium">45°C</strong>.
              S'installe sur n'importe quel siège — bureau, voiture, canapé.
              <br />
              Livré en <strong className="font-medium">3 à 7 jours</strong> depuis
              notre entrepôt en Allemagne 🇩🇪.
            </p>

            {/* Variant selector */}
            <div className="mt-8">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#8a8378] mb-3">
                Choisis ta finition
              </div>
              <div className="grid grid-cols-2 gap-3">
                {product.variants.map((v) => {
                  const selected = v.id === variantId;
                  const promo = getPromoPricing(product.id, v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      data-testid={`promo-variant-${v.id}`}
                      onClick={() => setVariantId(v.id)}
                      className={`relative text-left p-4 rounded-lg border transition-all ${
                        selected
                          ? "border-[#3D2B1F] bg-white shadow-[0_4px_20px_-8px_rgba(61,43,31,0.25)]"
                          : "border-[#dcd7ce] bg-[#F5EFE6] hover:border-[#a89482]"
                      }`}
                    >
                      {promo && (
                        <span
                          data-testid={`promo-variant-${v.id}-badge`}
                          className="absolute -top-2 -right-2 text-[10px] tracking-[0.14em] uppercase bg-[#B45C3F] text-white px-2 py-0.5 rounded-full font-medium"
                        >
                          -{promo.discountPct}%
                        </span>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="font-serif-display text-lg">
                          {v.name.fr}
                        </span>
                        {selected && (
                          <Check size={16} strokeWidth={2.2} className="text-[#3D2B1F]" />
                        )}
                      </div>
                      <div className="mt-1 text-[13px] text-[#4a4a4a] leading-snug">
                        {v.desc.fr}
                      </div>
                      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                        {promo ? (
                          <>
                            <span className="font-serif-display text-xl text-[#B45C3F]">
                              {promo.promo.toFixed(2)} €
                            </span>
                            <span
                              className="text-[13px] text-[#8a8378] line-through"
                              data-testid={`promo-variant-${v.id}-compare`}
                            >
                              {promo.base.toFixed(2)} €
                            </span>
                          </>
                        ) : (
                          <span className="font-serif-display text-xl text-[#1A1A1A]">
                            {v.price.toFixed(2)} €
                          </span>
                        )}
                        {v.badge?.fr && (
                          <span className="text-[10px] tracking-[0.14em] uppercase bg-[#3D2B1F] text-white px-2 py-1 rounded-full">
                            {v.badge.fr}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {getPromoPricing(product.id, variant.id) && (
                <div
                  data-testid="promo-banner"
                  className="mt-3 inline-flex items-center gap-2 bg-[#FCE9DC] text-[#8B3E1B] border border-[#EDCEB4] px-3 py-1.5 rounded-full text-[12px] font-medium"
                >
                  🎁 {getPromoBanner("fr")}
                </div>
              )}
            </div>

            {/* Primary CTA */}
            <button
              type="button"
              onClick={buyNow}
              data-testid="promo-cta-primary"
              className="mt-8 w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#3D2B1F] text-white px-10 py-5 rounded-md text-[13px] md:text-[14px] tracking-[0.22em] uppercase font-medium hover:bg-[#1A1A1A] hover:-translate-y-0.5 transition-all shadow-[0_10px_30px_-10px_rgba(61,43,31,0.55)]"
            >
              {(() => {
                const promo = getPromoPricing(product.id, variant.id);
                const price = promo ? promo.promo : variant.price;
                return <>Commander — {price.toFixed(2)} €</>;
              })()}
              <ArrowRight size={16} strokeWidth={2} />
            </button>

            {/* Trust micro-badges */}
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-[#4a4a4a]">
              <span className="inline-flex items-center gap-1.5">
                <Lock size={13} strokeWidth={1.6} /> Paiement 100% sécurisé
              </span>
              <span className="inline-flex items-center gap-1.5">
                <RotateCcw size={13} strokeWidth={1.6} /> Retour 30 jours
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={13} strokeWidth={1.6} /> Garantie 2 ans
              </span>
            </div>
          </div>

          {/* Hero image */}
          <div className="order-1 md:order-2 relative">
            <div className="aspect-[4/5] bg-[#ebe7df] rounded-md overflow-hidden">
              <img
                src={heroImage}
                alt="Coussin Massant Chauffant myFLOVEA installé sur un siège auto"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                width="1000"
                height="1250"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-white border border-[#dcd7ce] rounded-md px-4 py-3 shadow-[0_10px_25px_-15px_rgba(0,0,0,0.25)]">
              <div className="flex items-center gap-2">
                <div className="flex text-[#B45C3F]">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <span className="text-[13px] font-medium">Qualité premium</span>
              </div>
              <div className="text-[11px] text-[#4a4a4a] mt-0.5">
                Expédié d'Europe · Certifié CE
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS STRIP */}
      <section className="bg-[#3D2B1F] text-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-8 md:py-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {[
            { icon: Flame, title: "Chaleur 45°C", sub: "Détente immédiate" },
            { icon: Waves, title: "9 modes", sub: "Massage complet" },
            { icon: Truck, title: "3 à 7 jours", sub: "Depuis l'Allemagne" },
            { icon: ShieldCheck, title: "30 jours", sub: "Satisfait ou remboursé" },
          ].map((v, i) => {
            const Icon = v.icon;
            return (
              <div key={i} data-testid={`promo-value-${i}`} className="flex items-start gap-3">
                <Icon size={26} strokeWidth={1.5} className="mt-1 text-[#EFE7DA]" />
                <div>
                  <div className="font-serif-display text-lg md:text-xl leading-tight">
                    {v.title}
                  </div>
                  <div className="text-[12px] tracking-[0.14em] uppercase text-[#D4C4B0] mt-0.5">
                    {v.sub}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="max-w-[900px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center">
        <div className="eyebrow text-[#8B6343] mb-5">Le vrai problème</div>
        <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight text-[#1A1A1A]">
          Tu passes 8 heures assis. Ton corps encaisse tout.
        </h2>
        <p className="mt-6 text-[17px] leading-relaxed text-[#3D2B1F]">
          Bureau, trajets en voiture, canapé le soir : la position assise prolongée
          comprime tes disques intervertébraux, crispe tes trapèzes et bloque ta
          circulation. À la fin de la journée, ton dos t'envoie un message —
          <em> et généralement, tu le remets au lendemain.</em>
        </p>
        <p className="mt-4 text-[15px] leading-relaxed text-[#4a4a4a]">
          Le Coussin Massant Chauffant myFLOVEA a été conçu pour transformer
          n'importe quel siège en <strong>vrai fauteuil de récupération</strong>,
          en moins d'une minute.
        </p>
      </section>

      {/* FEATURES DEEP-DIVE */}
      <section className="bg-[#F5EFE6] border-y border-[#dcd7ce]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-24 space-y-16 md:space-y-24">
          {[
            {
              tag: "Chaleur profonde",
              title: "45°C qui détendent, pas qui brûlent.",
              body:
                "3 niveaux d'intensité réglables. La chaleur enveloppe tes trapèzes et le bas du dos, dilate les vaisseaux et relâche les fibres musculaires contractées. En 5 minutes, tu sens la différence.",
              img: officeImage,
              alt: "Femme au bureau utilisant le Coussin Massant Chauffant myFLOVEA",
            },
            {
              tag: "9 modes de massage",
              title: "Vibrations ciblées, du léger au profond.",
              body:
                "9 combinaisons de zones (nuque, milieu du dos, lombaires, cuisses) et 3 intensités. Tu choisis ton mode à la télécommande, tu poses le coussin sur ton siège, tu appuies. C'est tout.",
              img: remoteImage,
              alt: "Télécommande filaire du Coussin Massant Chauffant",
            },
            {
              tag: "S'adapte à tous les sièges",
              title: "Ton siège de bureau. Ta voiture. Ton canapé.",
              body:
                "Sangles élastiques réglables. Prise USB & prise allume-cigare 12V incluses. Emporte-le en week-end, laisse-le au bureau, monte-le dans la voiture — il te suit partout où ton dos a besoin de récupérer.",
              img: seatImage,
              alt: "Coussin Massant Chauffant installé sur un fauteuil velours",
            },
          ].map((f, i) => (
            <div
              key={i}
              data-testid={`promo-feature-${i}`}
              className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${
                i % 2 === 1 ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className="md:[direction:ltr]">
                <div className="aspect-[4/3] bg-[#ebe7df] rounded-md overflow-hidden">
                  <img
                    src={f.img}
                    alt={f.alt}
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="600"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="md:[direction:ltr]">
                <div className="eyebrow text-[#8B6343] mb-3">{f.tag}</div>
                <h3 className="font-serif-display text-2xl md:text-3xl lg:text-4xl leading-tight text-[#1A1A1A]">
                  {f.title}
                </h3>
                <p className="mt-5 text-[16px] leading-relaxed text-[#3D2B1F]">
                  {f.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-[1000px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="text-center mb-12">
          <div className="eyebrow text-[#8B6343] mb-4">Ta routine détente</div>
          <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight text-[#1A1A1A]">
            10 minutes. Trois gestes. C'est tout.
          </h2>
        </div>
        <ol className="grid md:grid-cols-3 gap-6 md:gap-8">
          {[
            {
              n: "01",
              title: "Installe-le",
              body:
                "Pose le coussin sur ton siège, ajuste les sangles. 30 secondes chrono.",
            },
            {
              n: "02",
              title: "Choisis ton mode",
              body:
                "Chaleur + intensité + zone. La télécommande fait le reste.",
            },
            {
              n: "03",
              title: "Détends-toi",
              body:
                "Ferme les yeux 10 minutes. Ton dos et ta tête te remercieront.",
            },
          ].map((s, i) => (
            <li
              key={i}
              data-testid={`promo-step-${i}`}
              className="bg-white border border-[#dcd7ce] rounded-md p-6 md:p-8"
            >
              <div className="font-serif-display text-4xl md:text-5xl text-[#8B6343] leading-none">
                {s.n}
              </div>
              <div className="mt-4 font-serif-display text-xl md:text-2xl text-[#1A1A1A]">
                {s.title}
              </div>
              <p className="mt-3 text-[14px] leading-relaxed text-[#4a4a4a]">
                {s.body}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {/* REASSURANCE GRID */}
      <section className="bg-[#F5EFE6] border-y border-[#dcd7ce]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-14 md:py-20">
          <h2 className="text-center font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mb-10">
            Pourquoi tu peux commander les yeux fermés
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                icon: Package,
                title: "Expédié d'Allemagne",
                sub: "DHL suivi · 3 à 7 jours ouvrés",
              },
              {
                icon: RotateCcw,
                title: "30 jours pour tester",
                sub: "Satisfait ou remboursé, sans questions",
              },
              {
                icon: ShieldCheck,
                title: "Garantie 2 ans",
                sub: "Support français sous 24h",
              },
              {
                icon: Lock,
                title: "Paiement sécurisé",
                sub: "Stripe · Carte, Apple Pay, Google Pay",
              },
            ].map((r, i) => {
              const Icon = r.icon;
              return (
                <div
                  key={i}
                  data-testid={`promo-reassurance-${i}`}
                  className="text-center"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-white border border-[#dcd7ce] flex items-center justify-center">
                    <Icon size={22} strokeWidth={1.5} className="text-[#3D2B1F]" />
                  </div>
                  <div className="mt-4 font-serif-display text-lg text-[#1A1A1A]">
                    {r.title}
                  </div>
                  <div className="mt-1 text-[12px] leading-relaxed text-[#4a4a4a]">
                    {r.sub}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Instagram Reel */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-20">
        <InstagramReelCard
          thumbnail="/photos/coussin/coussin-siege-velour.webp"
          lang="fr"
        />
      </section>

      {/* FAQ */}
      <section className="max-w-[800px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="text-center mb-8">
          <div className="eyebrow text-[#8B6343] mb-4">Vos questions</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A]">
            Les réponses aux 6 questions qu'on nous pose le plus.
          </h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {[
            {
              q: "Quand vais-je recevoir mon coussin ?",
              a: "Ton coussin est expédié depuis notre entrepôt en Allemagne 🇩🇪 par DHL, sous 24h ouvrées après ta commande. Tu le reçois généralement en 3 à 7 jours ouvrés partout en Europe. Un email avec ton numéro de suivi t'est envoyé automatiquement.",
            },
            {
              q: "Et si je n'aime pas ?",
              a: "Tu as 30 jours après réception pour changer d'avis, sans avoir à te justifier. On te rembourse intégralement, retour offert. Écris à contact@myflovea.com, on t'envoie un bordereau prépayé.",
            },
            {
              q: "Tissu Standard ou Velours Premium ?",
              a: "Le Tissu Standard (79€) est parfait pour un usage quotidien intensif — bureau, voiture. Le Velours Premium (89€) est plus doux au toucher — idéal pour le canapé ou pour offrir. Les deux ont les 9 modes et la chaleur 45°C.",
            },
            {
              q: "Est-ce qu'il chauffe vraiment ?",
              a: "Oui — jusqu'à 45°C, 3 niveaux d'intensité. La chaleur est enveloppante, jamais brûlante, et se coupe automatiquement en cas de surchauffe. C'est un vrai geste de thermothérapie.",
            },
            {
              q: "Comment ça se branche ?",
              a: "Prise USB (bureau, powerbank) et adaptateur allume-cigare 12V (voiture) tous les deux inclus. Longueur du câble : 2,20 m pour aller partout.",
            },
            {
              q: "Est-ce vraiment garanti 2 ans ?",
              a: "Oui, 2 ans complets. Notre support français (basé à Paris) répond à chaque email en moins de 24h ouvrées. Aucune paperasse : ton numéro de commande suffit.",
            },
          ].map((f, i) => (
            <AccordionItem
              key={i}
              value={`q-${i}`}
              data-testid={`promo-faq-${i}`}
              className="border-b border-[#dcd7ce]"
            >
              <AccordionTrigger className="text-left font-serif-display text-lg md:text-xl py-5 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] leading-relaxed text-[#3D2B1F] pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#3D2B1F] text-white">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 bg-[#5a3f2d] text-[#EFE7DA] px-4 py-2 rounded-full text-[11px] tracking-[0.16em] uppercase mb-6">
            <Timer size={13} strokeWidth={1.8} />
            Expédié sous 24h ouvrées
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-tight">
            Ton dos t'attend.
            <br />
            <span className="italic text-[#EFE7DA]">Le tien.</span>
          </h2>
          <p className="mt-6 text-[16px] md:text-[18px] leading-relaxed text-[#D4C4B0] max-w-lg mx-auto">
            Livré chez toi en <strong className="text-white font-medium">3 à 7 jours</strong>.
            Tu testes 30 jours. Tu adores, ou tu es remboursé.
          </p>
          <button
            type="button"
            onClick={buyNow}
            data-testid="promo-cta-final"
            className="mt-10 inline-flex items-center gap-3 bg-white text-[#3D2B1F] px-10 py-5 rounded-md text-[13px] md:text-[14px] tracking-[0.22em] uppercase font-medium hover:bg-[#F5EFE6] hover:-translate-y-0.5 transition-all shadow-[0_10px_30px_-10px_rgba(0,0,0,0.45)]"
          >
            {(() => {
              const promo = getPromoPricing(product.id, variant.id);
              const price = promo ? promo.promo : variant.price;
              return <>Commander — {price.toFixed(2)} €</>;
            })()}
            <ArrowRight size={16} strokeWidth={2} />
          </button>
          {(() => {
            const activeAny = product.variants.some((v) => getPromoPricing(product.id, v.id));
            if (!activeAny) return null;
            return (
              <div
                data-testid="promo-cta-final-end-date"
                className="mt-4 text-[12px] text-[#EFE7DA]/80"
              >
                {getPromoBanner("fr")}
              </div>
            );
          })()}
          <div className="mt-6 text-[12px] text-[#D4C4B0] flex flex-wrap justify-center gap-x-5 gap-y-2">
            <span>🚚 Livré 3–7 jours</span>
            <span>↩️ Retour 30 jours</span>
            <span>🔒 Paiement sécurisé</span>
            <span>🛡️ Garantie 2 ans</span>
          </div>
        </div>
      </section>

      {/* MINIMAL LEGAL FOOTER */}
      <footer className="bg-[#1A1A1A] text-[#D4C4B0]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-6 pb-24 md:pb-6 text-[11px] flex flex-col md:flex-row items-center justify-between gap-3">
          <div>
            © {new Date().getFullYear()} myFLOVEA — EM GROUP SASU · SIRET 100 603 026 00012
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 justify-center">
            <Link to="/legal/cgv" data-testid="promo-footer-cgv" className="hover:text-white">
              CGV
            </Link>
            <Link to="/legal/mentions" data-testid="promo-footer-legal" className="hover:text-white">
              Mentions légales
            </Link>
            <Link to="/legal/privacy" data-testid="promo-footer-privacy" className="hover:text-white">
              Confidentialité
            </Link>
            <Link to="/contact" data-testid="promo-footer-contact" className="hover:text-white">
              Contact
            </Link>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#dcd7ce] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-3 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.2)]">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.14em] uppercase text-[#8a8378] leading-none">
            {variant.name.fr}
          </div>
          {(() => {
            const promo = getPromoPricing(product.id, variant.id);
            if (promo) {
              return (
                <div className="font-serif-display text-xl text-[#1A1A1A] leading-tight flex items-baseline gap-2">
                  <span className="text-[#B45C3F]">{promo.promo.toFixed(2)} €</span>
                  <span className="text-[12px] text-[#8a8378] line-through">
                    {promo.base.toFixed(2)} €
                  </span>
                </div>
              );
            }
            return (
              <div className="font-serif-display text-xl text-[#1A1A1A] leading-tight">
                {variant.price.toFixed(2)} €
              </div>
            );
          })()}
        </div>
        <button
          type="button"
          onClick={buyNow}
          data-testid="promo-sticky-cta"
          className="bg-[#3D2B1F] text-white px-6 py-4 rounded-md text-[11px] tracking-[0.2em] uppercase font-medium flex items-center gap-2 min-h-[52px] active:translate-y-0.5"
        >
          Commander
          <ArrowRight size={13} />
        </button>
      </div>

      <WhatsAppButton lang="fr" />
    </div>
  );
};

export default PromoCoussin;
